import { Add } from '@mui/icons-material';
import { Box, Button, Divider } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';

import { useDataGridAutoHeightToScroll } from '~/components/SolaForceDataGrid/hooks/useDataGridAutoHeightToScroll';
import { useDataGridCRUDActions } from '~/components/SolaForceDataGrid/hooks/useDataGridCRUDActions';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { DocumentPrintMenu } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/components/DocumentPrintMenu';
import { DocumentsEntry } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/data';
import { useDocumentsTable } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/useDocumentsTable';
import { useDocumentsTableColumns } from '~/scenes/PeopleModule/components/Tabs/DocumentsTab/DocumentsTable/useDocumentsTableColumns';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

export type ScheduledEmploymentMenuOption = {
  startDate: string | Date;
  endDate: string | Date;
  id: number;
};

export const DocumentsTable = () => {
  const { t } = useTranslation();

  const {
    isLoading,
    dataGridProps: dataProps,
    canPrintDocument,
    columnsConfig,
    crudConfig,
  } = useDocumentsTable();

  const columnProps = useDocumentsTableColumns(columnsConfig);

  const {
    dataGridProps,
    toolbarProps: { addNew },
  } = useDataGridCRUDActions<DocumentsEntry, DocumentsEntry['id']>({
    dataGridProps: {
      ...dataProps,
      ...columnProps,
    },
    config: crudConfig,
  });

  const { dataGridProps: autoHeightProps, containerProps } =
    useDataGridAutoHeightToScroll({
      rowsCount: dataGridProps.rows.length,
      settings: {
        scrollRowCount: 10,
        rowHeight: 36,
        headerRowHeight: 39,
      },
    });

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box>
      <SolaForceHeading heading={t('laEmpDocs')}>
        {addNew.available && (
          <Button
            color="primary"
            startIcon={<Add />}
            onClick={() => addNew.onAddNewRow()}
            disabled={addNew.disabled}
          >
            {t('Add')}
          </Button>
        )}
        <Divider
          orientation={'vertical'}
          variant={'middle'}
          sx={{ height: 'auto', margin: '8px 0' }}
        />
        {canPrintDocument && <DocumentPrintMenu />}
      </SolaForceHeading>
      <Box {...containerProps}>
        <DataGridPremium
          {...dataGridProps}
          {...autoHeightProps}
          density={'compact'}
          initialState={{
            pinnedColumns: { right: ['approve', 'actions'] },
            columns: {
              columnVisibilityModel: {
                fileSize: false,
              },
            },
          }}
          hideFooter={true}
        />
      </Box>
    </Box>
  );
};
